/**
 * LeadsPage.tsx — the Leads list cockpit (story E0-BO-LS7).
 *
 * Consumes `GET /api/leads` through the injected API client and renders the
 * newest-first list with filters, client-side-hash search (Q5), and KEYSET
 * pagination via the envelope's opaque `next_cursor` ("load more"; null =
 * end-of-list — NO offset/page-number assumptions). Default load sends no
 * status filter, so the BFF returns the active/needs-attention scope (Q2); the
 * status checkboxes widen to terminal states.
 *
 * First-class states (spec §Error Handling): loading, empty, and a clean error
 * for the BFF's 502 "source unavailable" — an operator-readable message + retry,
 * never a crash or a blank page. A 401/403 renders the shared UnauthorizedScreen.
 *
 * Each row exposes a link to `#leads/<id>` — the navigation SEAM for the LS8
 * detail view; this story does not render detail.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ApiClient, LeadsQuery } from '../../api/client';
import type { LeadListItem } from '../../api/types';
import { UnauthorizedScreen } from '../../auth/UnauthorizedScreen';
import { loadPersistedFilters, savePersistedFilters } from '../../lib/persistFilters';
import { LeadStatusBadge } from './LeadStatusBadge';
import { LeadsFilters } from './LeadsFilters';
import { NewLeadForm } from './NewLeadForm';
import { formatDateTime } from './formatDateTime';
import { useLeadsQuery } from './useLeadsQuery';
import { Button } from '../../components/Button';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import { EmptyState } from '../../components/EmptyState';
import { Notice } from '../../components/Notice';
import { Skeleton } from '../../components/Skeleton';
import floating from '../../lib/floating';

export interface LeadsPageProps {
  apiClient: ApiClient;
}

const DEFAULT_QUERY: LeadsQuery = { limit: 50 };
/** sessionStorage key for the last-applied Leads filter set (search stripped). */
const LEADS_FILTERS_KEY = 'tcn.backoffice.leads.filters.v1';

// Persist coarse selections only — NEVER a (hashed) search term or cursor.
const persistableQuery = (query: LeadsQuery): LeadsQuery => {
  const rest = { ...query };
  delete rest.search;
  delete rest.cursor;
  return rest;
};

const initialQuery = (): LeadsQuery =>
  loadPersistedFilters<LeadsQuery>(LEADS_FILTERS_KEY) ?? DEFAULT_QUERY;
const LeadsTable = ({ items, locale }: { items: LeadListItem[]; locale: string }) => {
  const { t } = useTranslation();
  const columns: DataTableColumn<LeadListItem>[] = [
    {
      key: 'status',
      header: t('leads.columns.status'),
      renderCell: (lead) => <LeadStatusBadge status={lead.status} />,
    },
    {
      key: 'product',
      header: t('leads.columns.product'),
      renderCell: (lead) => <span className="text-ink-2">{lead.product_code ?? '—'}</span>,
    },
    {
      key: 'channel',
      header: t('leads.columns.channel'),
      renderCell: (lead) => <span className="text-ink-2">{lead.channel}</span>,
    },
    {
      key: 'created',
      header: t('leads.columns.created'),
      renderCell: (lead) => (
        <span className="text-ink-2">{formatDateTime(lead.created_at, locale)}</span>
      ),
    },
    {
      key: 'statusUpdated',
      header: t('leads.columns.statusUpdated'),
      renderCell: (lead) => (
        <span className="text-ink-2">{formatDateTime(lead.status_updated_at, locale)}</span>
      ),
    },
    {
      key: 'name',
      header: t('leads.columns.name'),
      link: true,
      renderCell: (lead) => (
        <a
          href={`#leads/${lead.id}`}
          data-testid="lead-link"
          aria-label={t('leads.viewLead', { name: lead.display_name ?? lead.id })}
          className="rounded text-gold underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-outline-focus"
        >
          {lead.display_name ?? '—'}
        </a>
      ),
    },
  ];
  return (
    <DataTable
      columns={columns}
      rows={items}
      rowKey={(lead) => lead.id}
      caption={t('leads.tableLabel')}
      data-testid="leads-table"
      rowTestId="lead-row"
    />
  );
};

export const LeadsPage = ({ apiClient }: LeadsPageProps) => {
  const { t, i18n } = useTranslation();
  const { items, nextCursor, approxTotal, phase, loadingMore, errored, load, loadMore } =
    useLeadsQuery(apiClient);

  // The last applied filter set — seeded from the remembered set so a returning
  // operator lands on the view they left. Re-run on Retry without re-deriving it.
  const [appliedQuery, setAppliedQuery] = useState<LeadsQuery>(initialQuery);
  const didMount = useRef(false);
  // LS12: the "New lead" form is mounted inline, toggled by a CTA — no route
  // change (the existing `#leads/<id>` route already covers the post-create
  // navigation to the new record's detail view).
  const [showNewLeadForm, setShowNewLeadForm] = useState(false);

  // Initial load: the remembered filter set, else the default active scope (Q2).
  useEffect(() => {
    if (didMount.current) return;
    didMount.current = true;
    floating(load(appliedQuery));
  }, [load, appliedQuery]);

  const handleApply = useCallback(
    (query: LeadsQuery) => {
      setAppliedQuery(query);
      savePersistedFilters(LEADS_FILTERS_KEY, persistableQuery(query));
      floating(load(query));
    },
    [load],
  );

  const handleRetry = useCallback(() => {
    floating(load(appliedQuery));
  }, [appliedQuery, load]);

  // Global "refresh" hotkey (see AppShell) re-runs the current query.
  useEffect(() => {
    const onRefresh = () => floating(load(appliedQuery));
    window.addEventListener('tcn:refresh', onRefresh);
    return () => window.removeEventListener('tcn:refresh', onRefresh);
  }, [load, appliedQuery]);

  if (phase === 'unauthorized') {
    return <UnauthorizedScreen />;
  }

  const busy = phase === 'loading' || loadingMore;

  const renderBranch1 = () => {
    if (phase === 'loading' && items.length === 0)
      return <Skeleton data-testid="leads-loading" label={t('leads.loading')} />;
    if (phase === 'error' && items.length === 0)
      return (
        <Notice
          tone="danger"
          data-testid="leads-error"
          data-focal="true"
          action={
            <Button data-testid="leads-retry" onClick={handleRetry}>
              {t('leads.retry')}
            </Button>
          }
        >
          {t('leads.errorBody')}
        </Notice>
      );
    if (items.length === 0)
      return <EmptyState data-testid="leads-empty" title={t('leads.empty')} />;
    return <LeadsTable items={items} locale={i18n.language} />;
  };

  return (
    <section aria-labelledby="leads-heading" className="flex flex-col gap-5">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 id="leads-heading" className="text-h1 text-ink-1">
          {t('leads.title')}
        </h1>
        <div className="flex items-center gap-3">
          <p role="status" className="text-meta text-ink-3" data-testid="leads-summary">
            {approxTotal != null && items.length > 0
              ? t('leads.range', { from: 1, to: items.length, total: approxTotal })
              : t('leads.showing', { count: items.length })}
          </p>
          {!showNewLeadForm ? (
            <button
              type="button"
              data-testid="leads-new-lead-cta"
              onClick={() => setShowNewLeadForm(true)}
              className="inline-flex min-h-11 items-center rounded-lg bg-gold px-4 py-1.5 text-body font-medium text-on-gold hover:bg-gold/90"
            >
              {t('leads.newLead.cta')}
            </button>
          ) : null}
        </div>
      </header>

      {showNewLeadForm ? (
        <NewLeadForm
          apiClient={apiClient}
          onCancel={() => setShowNewLeadForm(false)}
          onCreated={(leadId) => {
            setShowNewLeadForm(false);
            window.location.hash = `leads/${encodeURIComponent(leadId)}`;
          }}
        />
      ) : null}

      <LeadsFilters
        onApply={handleApply}
        busy={busy}
        applied={appliedQuery}
        locale={i18n.language}
      />

      {/* SINGLE error surface. With rows on screen (a refresh failed but the
          last-known list survives) it's a compact banner. With NO rows the same
          failure becomes the focal (elev-2 status-down ring) — the loudest thing
          on the panel — never both at once (dedup). */}
      {errored && items.length > 0 ? (
        <p
          role="alert"
          data-testid="leads-error-banner"
          className="rounded-lg bg-status-down/15 px-4 py-2 text-meta text-status-down ring-1 ring-inset ring-status-down/40"
        >
          {t('leads.errorBody')}
        </p>
      ) : null}

      {renderBranch1()}

      {items.length > 0 ? (
        <div className="flex items-center gap-3">
          {nextCursor != null ? (
            <button
              type="button"
              data-testid="leads-load-more"
              onClick={() => floating(loadMore())}
              disabled={loadingMore}
              aria-busy={loadingMore}
              className="inline-flex items-center rounded-lg bg-surface-2 px-4 py-1.5 max-md:min-h-11 text-body font-medium text-ink-1 ring-1 ring-inset ring-ink-3/20 hover:bg-surface-1 disabled:opacity-60"
            >
              {loadingMore ? t('leads.loadingMore') : t('leads.loadMore')}
            </button>
          ) : (
            <p data-testid="leads-end" className="text-meta text-ink-3">
              {t('leads.endOfList')}
            </p>
          )}
        </div>
      ) : null}
    </section>
  );
};
